/* ==========================================================================
   2ATG — header interaction

   Registers as the `nav` module, so it runs through the same mount/unmount
   registry as everything else and is torn down correctly when a merchant
   edits the header in the theme customizer. See atg-motion.js.

     - dropdown panels on bar items with children, with a 120ms close grace
       so a diagonal mouse path from the trigger to the panel does not
       dismiss it; keyboard and touch open on focus / first tap
     - the drawer: opens from the menu button, closes from its own close
       button, the scrim or Escape; locks page scroll; keeps focus inside
       while open and hands it back to the menu button on close
   ========================================================================== */

(function () {
  "use strict";

  if (!window.ATG) return;

  var GRACE = 120; // ms
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

  window.ATG.register("nav", function (root) {
    var items   = root.querySelectorAll(".atg-bar__item.has-panel");
    var burger  = root.querySelector(".atg-header__burger");
    var drawer  = root.querySelector(".atg-drawer");
    var panel   = drawer ? drawer.querySelector(".atg-drawer__panel") : null;
    var closers = drawer ? drawer.querySelectorAll("[data-atg-drawer-close]") : [];
    var toggles = root.querySelectorAll(".atg-drawer__toggle");
    var listeners = [];
    var closeTimer = null;
    var hideTimer = null;
    var openItem = null;

    function on(el, type, fn, opts) {
      el.addEventListener(type, fn, opts);
      listeners.push(function () { el.removeEventListener(type, fn, opts); });
    }

    /* ---- Dropdown panels -------------------------------------------- */

    function openPanel(item) {
      if (closeTimer) { clearTimeout(closeTimer); closeTimer = null; }
      if (openItem && openItem !== item) closePanel(openItem);

      var p    = item.querySelector(".atg-panel");
      var link = item.querySelector(".atg-bar__link");
      if (!p) return;

      p.hidden = false;
      // Next frame, so the transition has a start state to animate from.
      window.requestAnimationFrame(function () { item.classList.add("is-open"); });
      if (link) link.setAttribute("aria-expanded", "true");
      root.classList.add("has-open-panel");
      openItem = item;
    }

    function closePanel(item) {
      var p    = item.querySelector(".atg-panel");
      var link = item.querySelector(".atg-bar__link");
      item.classList.remove("is-open");
      if (link) link.setAttribute("aria-expanded", "false");

      // Wait out the fade before hiding, or it snaps.
      window.setTimeout(function () {
        if (!item.classList.contains("is-open") && p) p.hidden = true;
      }, 200);

      if (openItem === item) openItem = null;
      if (!root.querySelector(".atg-bar__item.is-open")) root.classList.remove("has-open-panel");
    }

    function scheduleClose(item) {
      if (closeTimer) clearTimeout(closeTimer);
      closeTimer = window.setTimeout(function () { closePanel(item); }, GRACE);
    }

    Array.prototype.forEach.call(items, function (item) {
      on(item, "mouseenter", function () { openPanel(item); });
      on(item, "mouseleave", function () { scheduleClose(item); });

      on(item, "focusin", function () { openPanel(item); });
      on(item, "focusout", function (e) {
        if (!item.contains(e.relatedTarget)) scheduleClose(item);
      });

      // Touch: the first tap opens rather than following the link.
      var link = item.querySelector(".atg-bar__link");
      if (link) {
        on(link, "click", function (e) {
          if (window.matchMedia("(hover: hover)").matches) return;
          if (!item.classList.contains("is-open")) {
            e.preventDefault();
            openPanel(item);
          }
        });
      }
    });

    /* ---- Drawer ------------------------------------------------------ */

    function isOpen() { return root.classList.contains("is-drawer-open"); }

    function setDrawer(open) {
      if (!drawer || !burger) return;
      if (hideTimer) { clearTimeout(hideTimer); hideTimer = null; }
      burger.setAttribute("aria-expanded", open ? "true" : "false");
      document.documentElement.style.overflow = open ? "hidden" : "";

      if (open) {
        drawer.hidden = false;
        // Force a layout so the slide has its off-screen start state, then
        // open. (A reflow rather than rAF: rAF does not run in a background
        // tab, which left the drawer un-hidden but never slid in.)
        void drawer.offsetWidth;
        root.classList.add("is-drawer-open");
        var first = panel && panel.querySelector(".atg-drawer__close");
        if (first) first.focus({ preventScroll: true });
      } else {
        root.classList.remove("is-drawer-open");
        hideTimer = window.setTimeout(function () {
          if (!isOpen()) drawer.hidden = true;
        }, 380);
      }
    }

    if (burger) {
      on(burger, "click", function () { setDrawer(!isOpen()); });
    }

    Array.prototype.forEach.call(closers, function (el) {
      on(el, "click", function () {
        setDrawer(false);
        if (burger) burger.focus({ preventScroll: true });
      });
    });

    // Following a link inside the drawer: close it, so an in-page anchor
    // (LOADOUTS -> /#loadout) does not land behind an open sheet.
    if (panel) {
      on(panel, "click", function (e) {
        var a = e.target.closest && e.target.closest("a[href]");
        if (a) setDrawer(false);
      });
    }

    Array.prototype.forEach.call(toggles, function (toggle) {
      on(toggle, "click", function () {
        var open = toggle.getAttribute("aria-expanded") === "true";
        var sub = toggle.nextElementSibling;
        toggle.setAttribute("aria-expanded", open ? "false" : "true");
        if (sub) sub.hidden = open;
      });
    });

    /* ---- Keyboard: Escape, and Tab kept inside the open drawer -------- */

    on(document, "keydown", function (e) {
      if (e.key === "Escape") {
        if (openItem) closePanel(openItem);
        if (isOpen()) {
          setDrawer(false);
          if (burger) burger.focus({ preventScroll: true });
        }
        return;
      }
      if (e.key !== "Tab" || !isOpen() || !panel) return;
      var nodes = Array.prototype.filter.call(panel.querySelectorAll(FOCUSABLE), function (n) {
        return n.offsetParent !== null;
      });
      if (!nodes.length) return;
      var firstEl = nodes[0], lastEl = nodes[nodes.length - 1];
      if (e.shiftKey && document.activeElement === firstEl) { e.preventDefault(); lastEl.focus(); }
      else if (!e.shiftKey && document.activeElement === lastEl) { e.preventDefault(); firstEl.focus(); }
    });

    /* ---- Teardown ---------------------------------------------------- */

    return function () {
      if (closeTimer) clearTimeout(closeTimer);
      if (hideTimer) clearTimeout(hideTimer);
      listeners.forEach(function (off) { off(); });
      listeners.length = 0;
      document.documentElement.style.overflow = "";
      root.classList.remove("has-open-panel", "is-drawer-open");
    };
  });
  /* ==================================================================
     MODULE: search
     The search icons open a panel that drops from the top instead of
     leaving the page. Typing asks Shopify's predictive search for the
     atg-predictive-search section (debounced, the previous request
     aborted) and shows it under the field; Enter goes to the full
     results page. Escape, the close button or the scrim closes it and
     focus returns to the icon. "/" opens it from anywhere on the page.
     ================================================================== */

  window.ATG.register("search", function (root) {
    var box = root.querySelector("[data-atg-search]");
    if (!box) return;
    var input = box.querySelector("[data-atg-search-input]");
    var results = box.querySelector("[data-atg-search-results]");
    var start = box.querySelector("[data-atg-search-start]");
    var clear = box.querySelector("[data-atg-search-clear]");
    var panel = box.querySelector(".atg-search__panel");
    var url = box.getAttribute("data-url") || "/search/suggest";
    var listeners = [];
    var timer = null;
    var hideTimer = null;
    var ctrl = null;
    var opener = null;
    var lastTerm = "";

    function on(el, type, fn, opts) {
      if (!el) return;
      el.addEventListener(type, fn, opts);
      listeners.push(function () { el.removeEventListener(type, fn, opts); });
    }

    function isOpen() { return box.classList.contains("is-open"); }

    function open(from) {
      if (isOpen()) return;
      opener = from || document.activeElement;
      clearTimeout(hideTimer);
      box.hidden = false;
      void box.offsetWidth; // commit the closed state so the transition runs
      box.classList.add("is-open");
      root.classList.add("is-search-open");
      document.documentElement.style.overflow = "hidden";
      input.focus({ preventScroll: true });
      if (input.value) input.select();
    }

    function close() {
      if (!isOpen()) return;
      box.classList.remove("is-open");
      root.classList.remove("is-search-open");
      document.documentElement.style.overflow = "";
      hideTimer = setTimeout(function () { box.hidden = true; }, 320);
      if (opener && opener.focus && opener.getAttribute("tabindex") !== "-1") opener.focus({ preventScroll: true });
    }

    function showStart(show) {
      if (start) start.hidden = !show;
      if (!show) return;
      results.innerHTML = "";
      box.classList.remove("has-results");
    }

    function query(term) {
      if (ctrl) ctrl.abort();
      ctrl = window.AbortController ? new AbortController() : null;
      box.classList.add("is-loading");
      var u = new URL(url, window.location.origin);
      u.searchParams.set("q", term);
      u.searchParams.set("section_id", "atg-predictive-search");
      u.searchParams.set("resources[type]", "product,collection,page,query");
      u.searchParams.set("resources[limit]", "8");
      u.searchParams.set("resources[options][unavailable_products]", "last");
      u.searchParams.set("resources[options][fields]", "title,product_type,variants.title,variants.sku,tag");
      fetch(u.toString(), { signal: ctrl ? ctrl.signal : undefined, headers: { "Accept": "text/html" } })
        .then(function (r) { if (!r.ok) throw new Error("HTTP " + r.status); return r.text(); })
        .then(function (html) {
          if (term !== lastTerm) return;
          var doc = new DOMParser().parseFromString(html, "text/html");
          var ps = doc.querySelector("[data-atg-ps]");
          results.innerHTML = ps ? ps.outerHTML : "";
          box.classList.add("has-results");
          box.classList.remove("is-loading");
        })
        .catch(function (err) {
          if (err && err.name === "AbortError") return;
          box.classList.remove("is-loading");
        });
    }

    function onInput() {
      var term = input.value.trim();
      if (clear) clear.hidden = !input.value;
      clearTimeout(timer);
      lastTerm = term;
      if (!term) { if (ctrl) ctrl.abort(); box.classList.remove("is-loading"); showStart(true); return; }
      showStart(false);
      timer = setTimeout(function () { query(term); }, 180);
    }

    on(input, "input", onInput);
    on(clear, "click", function () { input.value = ""; onInput(); input.focus(); });

    // Openers: the header icons (top row and pinned bar).
    on(root, "click", function (e) {
      var a = e.target.closest && e.target.closest("[data-atg-search-open]");
      if (a) {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
        e.preventDefault();
        open(a);
        return;
      }
      if (e.target.closest && e.target.closest("[data-atg-search-close]")) close();
    });

    on(document, "keydown", function (e) {
      if (e.key === "Escape" && isOpen()) { e.preventDefault(); close(); return; }
      if (e.key === "/" && !isOpen()) {
        var t = e.target;
        if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
        e.preventDefault();
        open(null);
      }
      // Keep Tab inside the panel while it is open.
      if (e.key === "Tab" && isOpen()) {
        var nodes = Array.prototype.filter.call(panel.querySelectorAll('a[href], button:not([disabled]):not([hidden]), input:not([type="hidden"])'), function (n) {
          return n.offsetParent !== null;
        });
        if (!nodes.length) return;
        var first = nodes[0], last = nodes[nodes.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    });

    return function () {
      clearTimeout(timer);
      clearTimeout(hideTimer);
      if (ctrl) ctrl.abort();
      listeners.forEach(function (off) { off(); });
      listeners.length = 0;
      if (isOpen()) { document.documentElement.style.overflow = ""; root.classList.remove("is-search-open"); }
    };
  });
})();
